/**
 * Telemetria de conexão da sala — diagnóstico de quedas.
 *
 * Grava um evento em `connectionDiagnostics` a cada transição relevante
 * (reconectando, reconectado, roomLeft, reconexão suave, retorno de segundo
 * plano). O objetivo é entender POR QUE um usuário caiu: o servidor não
 * enxerga o `roomLeft`/estado de rede do cliente, então capturamos aqui.
 *
 * Regras de ouro:
 * - Nunca lança exceção (telemetria não pode quebrar a sala).
 * - Best-effort: se a escrita falhar (ex.: regra/offline), apenas ignora.
 */

import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import {
    DROP_EVENT_TYPES,
    RECONNECT_END_TYPES,
    RECONNECT_START_TYPES,
    type WebRtcSnapshot,
} from "./webrtcStats";

export type ConnectionEventType =
    | "reconnecting"
    | "reconnected"
    | "roomLeft"
    | "softReconnectStart"
    | "softReconnectSuccess"
    | "softReconnectFail"
    | "reloadFallback"
    | "visibilityResume"
    | "visibilityHidden"
    | "pageFreeze"
    | "pageResume";

// Rastreia transições de visibilidade para detectar "voltou do segundo plano".
let lastHiddenAt: number | null = null;
let backgroundReturnAt: number | null = null;
let visibilityTrackerInstalled = false;
/** Início do ciclo de reconexão atual (ms epoch). */
let reconnectStartedAt: number | null = null;

/** Marca retorno do segundo plano (chamado pelo Room ao detectar visibility). */
export function markBackgroundReturn(): void {
    backgroundReturnAt = Date.now();
}

function ensureVisibilityTracker(): void {
    if (visibilityTrackerInstalled || typeof document === "undefined") return;
    visibilityTrackerInstalled = true;
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
            lastHiddenAt = Date.now();
        } else if (document.visibilityState === "visible" && lastHiddenAt != null) {
            backgroundReturnAt = Date.now();
        }
    });
}

/** Evita gravar o mesmo evento 2× (ex.: dois DyteMeet montados). */
const recentEventKeys = new Map<string, number>();
const DEDUP_WINDOW_MS = 2500;

function shouldSkipDuplicate(type: string, ctx: ConnectionEventContext): boolean {
    const key = [
        type,
        ctx.roomId,
        ctx.userId ?? "",
        ctx.roomLeftState ?? "",
        ctx.reconnectAttempt ?? "",
        ctx.meetingExists ?? "",
        ctx.socketReconnectionAttempt ?? "",
    ].join("|");
    const now = Date.now();
    const prev = recentEventKeys.get(key);
    if (prev != null && now - prev < DEDUP_WINDOW_MS) {
        return true;
    }
    recentEventKeys.set(key, now);
    // Limpa entradas antigas ocasionalmente.
    if (recentEventKeys.size > 80) {
        for (const k of Array.from(recentEventKeys.keys())) {
            const t = recentEventKeys.get(k)!;
            if (now - t > DEDUP_WINDOW_MS * 4) {
                recentEventKeys.delete(k);
            }
        }
    }
    return false;
}

function getNetworkInfo(): Record<string, unknown> | null {
    if (typeof navigator === "undefined") return null;
    const connection = (navigator as unknown as {
        connection?: {
            effectiveType?: string;
            downlink?: number;
            rtt?: number;
            saveData?: boolean;
            type?: string;
        };
    }).connection;
    if (!connection) return null;
    return {
        effectiveType: connection.effectiveType ?? null,
        downlink: typeof connection.downlink === "number" ? connection.downlink : null,
        rtt: typeof connection.rtt === "number" ? connection.rtt : null,
        saveData: connection.saveData ?? null,
        type: connection.type ?? null,
    };
}

export interface ConnectionEventContext {
    roomId: string;
    userId?: string | null;
    role?: string | null;
    device?: string | null;
    roomLeftState?: string | null;
    socketState?: string | null;
    mediaState?: string | null;
    reconnectAttempt?: number | null;
    /** Doc dyteMeetings/{roomId} existia no momento do evento (diagnóstico de roomLeft=ended). */
    meetingExists?: boolean | null;
    /** ICE/PC ainda connected no momento do roomLeft (doc pode ter sumido antes). */
    mediaStillConnected?: boolean | null;
    /** Motor de vídeo: cloudflare (padrão) ou livekit (piloto /sala-beta/). */
    engine?: "cloudflare" | "livekit" | null;
    /** Tentativa nativa do socket RTK (socketConnectionUpdate.reconnectionAttempt). */
    socketReconnectionAttempt?: number | null;
    /** Snapshot WebRTC (packet loss, jitter, RTT, bitrates, candidate pair). */
    webrtc?: WebRtcSnapshot | null;
}

export function logConnectionEvent(
    type: ConnectionEventType,
    ctx: ConnectionEventContext,
): void {
    try {
        ensureVisibilityTracker();

        if (shouldSkipDuplicate(type, ctx)) {
            return;
        }

        const now = Date.now();
        const visibilityState =
            typeof document !== "undefined" ? document.visibilityState : null;

        // "Veio do segundo plano" se retornou nos últimos 30s (janela curta pós-volta).
        const cameFromBackground =
            backgroundReturnAt != null && now - backgroundReturnAt < 30_000;
        const msSinceHidden = lastHiddenAt != null ? now - lastHiddenAt : null;
        const msSinceBackgroundReturn =
            backgroundReturnAt != null ? now - backgroundReturnAt : null;

        const network = getNetworkInfo();
        const webrtc = ctx.webrtc ?? null;
        const isDrop = DROP_EVENT_TYPES.has(type);

        // Tempo de reconexão: marca início e fecha no sucesso/falha.
        if (RECONNECT_START_TYPES.has(type) && reconnectStartedAt == null) {
            reconnectStartedAt = now;
        }
        let reconnectDurationMs: number | null = null;
        if (RECONNECT_END_TYPES.has(type) && reconnectStartedAt != null) {
            reconnectDurationMs = now - reconnectStartedAt;
            reconnectStartedAt = null;
        }

        // Campos no topo do documento — consulta fácil.
        const rttMs =
            webrtc?.rttMs ??
            (typeof network?.rtt === "number" ? network.rtt : null);
        const packetLossPercent = webrtc?.packetLossPercent ?? null;
        const packetsLost = webrtc?.packetsLost ?? null;
        const icePath = webrtc?.icePath ?? null;
        const iceCandidateType =
            webrtc?.candidatePair?.localCandidateType ?? null;
        const usingRelay = webrtc?.candidatePair?.usingRelay ?? null;

        void addDoc(collection(db, "connectionDiagnostics"), {
            type,
            isDrop,
            roomId: ctx.roomId,
            userId: ctx.userId ?? null,
            role: ctx.role ?? null,
            device: ctx.device ?? null,
            roomLeftState: ctx.roomLeftState ?? null,
            socketState: ctx.socketState ?? null,
            mediaState: ctx.mediaState ?? null,
            reconnectAttempt: ctx.reconnectAttempt ?? null,
            meetingExists: ctx.meetingExists ?? null,
            mediaStillConnected: ctx.mediaStillConnected ?? null,
            engine: ctx.engine ?? "cloudflare",
            socketReconnectionAttempt: ctx.socketReconnectionAttempt ?? null,
            // Prioridade 1 — métricas de queda / WebRTC
            rttMs,
            packetLossPercent,
            packetsLost,
            icePath,
            iceCandidateType,
            usingRelay,
            reconnectDurationMs,
            iceConnectionState: webrtc?.iceConnectionState ?? null,
            connectionState: webrtc?.connectionState ?? null,
            iceGatheringState: webrtc?.iceGatheringState ?? null,
            participantCount: webrtc?.participantCount ?? null,
            streamCount: webrtc?.streamCount ?? null,
            localAudioEnabled: webrtc?.localAudioEnabled ?? null,
            localVideoEnabled: webrtc?.localVideoEnabled ?? null,
            webrtc,
            visibilityState,
            cameFromBackground,
            msSinceLastHidden: msSinceHidden,
            msSinceBackgroundReturn,
            online: typeof navigator !== "undefined" ? navigator.onLine : null,
            network,
            userAgent:
                typeof navigator !== "undefined"
                    ? navigator.userAgent.slice(0, 256)
                    : null,
            clientTs: now,
            ts: serverTimestamp(),
        }).catch(() => {});
    } catch {
        // Telemetria é reforço: nunca deve quebrar a sala.
    }
}
